import { z } from "zod";

// Bloom wallet — THB-denominated balance per user. Top-ups go through Stripe
// (min ฿10 to clear the THB charge floor); spends are internal debits at the
// canonical ฿9 per-episode unit price or per-series bundle price. Stripe
// payment processing only ever fires on the top-up, never on episode unlock.

export const walletSchema = z.object({
  // One wallet per user — _id mirrors the user's _id (hex ObjectId string).
  _id: z.string(),
  userId: z.string(),
  balanceThb: z.number().nonnegative(),
  // Total ever credited (lifetime top-ups + promo grants). Useful for
  // analytics and refund accounting.
  totalCreditedThb: z.number().nonnegative().default(0),
  // Total ever debited (lifetime spends). balanceThb = totalCredited - totalSpent.
  totalSpentThb: z.number().nonnegative().default(0),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Wallet = z.infer<typeof walletSchema>;

// Append-only ledger row. The wallet `balanceThb` is denormalized for fast
// reads; this collection is the source of truth and an hourly reconciler
// (round-6 cron) compares the two.
export const walletTransactionKindSchema = z.enum([
  "topup", // +THB from a Stripe top-up
  "promo", // +THB granted by admin or campaign
  "refund", // +THB returned after a refund
  "unlock-episode", // -THB for a single episode
  "unlock-bundle", // -THB for a whole-series bundle
  "adjustment", // ± admin manual correction
]);
export type WalletTransactionKind = z.infer<typeof walletTransactionKindSchema>;

export const walletTransactionSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  kind: walletTransactionKindSchema,
  // Signed THB amount: positive for credit, negative for debit.
  amountThb: z.number(),
  // Balance after this transaction applied. Lets the UI render a running
  // balance column without a re-aggregate.
  balanceAfterThb: z.number().nonnegative(),
  // Idempotency key — same key applied twice is a no-op. For top-ups this is
  // the Stripe session id; for unlocks it's `${kind}:${userId}:${targetId}`.
  idempotencyKey: z.string(),
  // Pointer back to the cause: stripe session, episode id, series id, etc.
  refKind: z.enum(["stripe_session", "episode", "series", "promo_code", "admin_action"]).optional(),
  refId: z.string().optional(),
  // Free-form note for promo/adjustment rows.
  note: z.string().optional(),
  // Audit trail for admin-driven adjustments.
  actorId: z.string().optional(),
  createdAt: z.coerce.date(),
});
export type WalletTransaction = z.infer<typeof walletTransactionSchema>;
